"use client";

import { useRef, useEffect, useState } from "react";
import { storedOceanCurrentMs } from "@/lib/conditions/daily-obs-types";
import { formatWind, type WindUnit } from "@/lib/conditions/forecast-presentation";
import type { NormalisedHour } from "@/lib/conditions/daily-obs-types";
import type { NullableHourlyScore } from "@/lib/conditions/session-window-universal";
import { weatherCodeEmoji, weatherCodeLabel } from "@/lib/conditions/weather-code-display";
import { degToCompass } from "@/lib/conditions/scoring";
import { _factors } from "@/lib/conditions/piste-scoring";
import { visColour, visLabel } from "@/lib/conditions/visibility-strip";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import { isSkiSport, skiLabel10 } from "@/lib/conditions/ski-labels-v3";
import type { StoredHourlyRangeFields } from "@/lib/conditions/stored-precomputed-scores";
import { skiHourWithheldLabel, skiLikelyRange10 } from "./ski-board-display";
import {
  mtbSoilMoistureLabel,
  weightedMtbSoilMoisture,
} from "@/lib/conditions/mtb-soil-moisture";

/**
 * An hourly point. Ski v3 lean points may also carry the P10 and P90 (`lo`, `hi`, 0 to 10) and
 * `st` when the hour's score is withheld (Closed, or No lift access); revision 2 points have
 * none of them and render as before.
 */
type TimelineHour = NullableHourlyScore & StoredHourlyRangeFields;

type Props = {
  scores: TimelineHour[];
  hours: NormalisedHour[];
  sport: string;
  localHour?: number;
  bestHour?: number | null;
  selectedHour?: number;
  onSelectHour?(hour: number): void;
  windUnit?: WindUnit;
};

function formatHour(h: number): string {
  if (h === 0 || h === 24) return "12am";
  if (h === 12) return "12pm";
  return h < 12 ? `${h}am` : `${h - 12}pm`;
}

const FLAT_SPORTS = new Set(["surf", "foil", "downwind-foil", "wing-foil", "wind"]);
const MOUNTAIN_SPORTS = new Set(["piste", "off-piste", "touring", "mtb"]);

// Top-band vocabulary per sport (shared thresholds, per-sport words)
const EPIC_TOP_SPORTS = new Set(["surf", "wind", "mtb", "sailing"]);

function ratingFromScore(score: number, sport: string): { label: string; bg: string; text: string } {
  // Shared platform bands (9/7.5/6.5/5/3.5/2) so the hourly chips agree
  // with the sport's own label function and the headline badge.
  if (score >= 9) {
    const top = sport === "piste" ? "Hero Snow" : EPIC_TOP_SPORTS.has(sport) ? "Epic" : "Hero";
    return { label: top, bg: "bg-emerald-100", text: "text-emerald-800" };
  }
  if (score >= 7.5) return { label: "Firing", bg: "bg-emerald-100", text: "text-emerald-800" };
  if (score >= 6.5) return { label: "Great", bg: "bg-green-100", text: "text-green-800" };
  if (score >= 5) return { label: "Good", bg: "bg-lime-100", text: "text-lime-800" };
  if (score >= 3.5) return { label: "Fair", bg: "bg-amber-100", text: "text-amber-800" };
  if (score >= 2) return { label: "Poor", bg: "bg-orange-100", text: "text-orange-800" };
  const zeroLabel = FLAT_SPORTS.has(sport) ? "Flat" : "Poor";
  return { label: zeroLabel, bg: "bg-slate-100", text: "text-slate-600" };
}

/** Hour chip label: the sport's own vocabulary for ski (colour stays on the platform bands). */
function hourLabel(score: number, sport: string, rating: { label: string }): string {
  return isSkiSport(sport) ? skiLabel10(sport, score) : rating.label;
}

/** P10 to P90 band for a v3 ski hour on a 0 to 100 track, with a tick at the score. */
function HourRange({ lo, hi, score }: { lo: number; hi: number; score: number }) {
  const clamp = (v: number) => Math.max(0, Math.min(100, v * 10));
  const left = clamp(Math.min(lo, hi));
  const right = clamp(Math.max(lo, hi));
  const likely = skiLikelyRange10(lo, hi);
  return (
    <div
      className="relative mx-1.5 mt-1 h-1.5 rounded-full bg-slate-100"
      title={likely ?? undefined}
      role="img"
      aria-label={likely ?? "Likely range"}
      data-ski-range
    >
      <div
        className="absolute inset-y-0 rounded-full bg-sky-300/70"
        style={{ left: `${left}%`, width: `${Math.max(2, right - left)}%` }}
      />
      <div
        className="absolute top-[-1px] bottom-[-1px] w-[2px] rounded-full bg-navy"
        style={{ left: `calc(${clamp(score)}% - 1px)` }}
      />
    </div>
  );
}

function windArrow(deg: number): string {
  const dirs: Record<string, string> = {
    N: "↓", NE: "↙", E: "←", SE: "↖", S: "↑", SW: "↗", W: "→", NW: "↘",
  };
  return dirs[degToCompass(deg)] ?? "·";
}

export function sportMetrics(h: NormalisedHour, sport: string, unit: WindUnit = "kmh"): string[] {
  const w = h.weather;
  const m = h.marine;
  const reading = (value: number | null | undefined, suffix: string, decimals = 1) => value == null ? `${suffix} unavailable` : `${value.toFixed(decimals)}${suffix}`;
  const wind = `${windArrow(w.wind_dir_10m_deg)} ${formatWind(w.wind_speed_10m_kmh, unit)}`;
  const gust = `Gusts ${formatWind(w.wind_gust_10m_kmh, unit)}`;
  const swell = `${reading(m?.swell_height_m ?? m?.wave_height_m, "m")} · ${reading(m?.swell_period_s, "s", 0)}`;
  const water = m?.sst_c == null ? "Water unavailable" : `${m.sst_c.toFixed(1)}°C water`;
  const current = reading(storedOceanCurrentMs(m), "m/s current", 2);
  switch (sport) {
    case "surf": case "bodyboarding": case "foil": case "sup-surf":
      return [swell, wind, water];
    case "wind": case "wing-foil":
      return [wind, gust, `${w.temp_c.toFixed(0)}°C air`];
    case "downwind-foil": case "sup-downwind":
      return [reading(m?.wind_wave_height_m, "m wind waves"), wind, gust,
        m?.wind_wave_dir_deg == null ? "Wave direction unavailable" : `Waves from ${degToCompass(m.wind_wave_dir_deg)}`];
    case "diving":
      return [`Entry ${swell}`, current, water];
    case "swimming": case "swim":
      return [water, reading(m?.wave_height_m, "m sea"), current, wind];
    case "sup": case "sup-touring":
      return [wind, gust, reading(m?.wave_height_m, "m sea"), current];
    case "piste": case "off-piste": case "touring":
      return [`${w.temp_c.toFixed(0)}°C`, wind,
        ...(w.visibility_m != null ? [`${(w.visibility_m / 1000).toFixed(1)}km visibility`] : []),
        ...((w.snowfall_cm ?? 0) > 0 ? [`${w.snowfall_cm!.toFixed(1)}cm new`] : [])];
    case "mtb": {
      const soil = mtbSoilMoistureLabel(weightedMtbSoilMoisture(w));
      return [soil ? `${soil} estimate` : "Surface unavailable", `${w.precip_mm.toFixed(1)}mm rain`, `${w.temp_c.toFixed(0)}°C`, wind];
    }
    default: return [wind, `${w.temp_c.toFixed(0)}°C`];
  }
}

function secondaryMetrics(h: NormalisedHour, unit: WindUnit): { label: string; value: string; highlight?: boolean }[] {
  const w = h.weather;
  const items: { label: string; value: string; highlight?: boolean }[] = [];
  if (w.apparent_temp_c !== null) items.push({ label: "Feels", value: `${w.apparent_temp_c.toFixed(0)}°` });
  items.push({ label: "Cloud", value: `${w.cloud_cover_pct}%` });
  if (w.precip_mm > 0) items.push({ label: "Rain", value: `${w.precip_mm.toFixed(1)}mm`, highlight: true });
  if (w.wind_gust_10m_kmh > w.wind_speed_10m_kmh * 1.2) items.push({ label: "Gust", value: formatWind(w.wind_gust_10m_kmh, unit) });
  if (w.visibility_m !== null) items.push({ label: "Vis", value: `${(w.visibility_m / 1000).toFixed(1)}km` });
  if (w.uv_index > 0 && w.is_day) items.push({ label: "UV", value: `${w.uv_index.toFixed(0)}`, highlight: w.uv_index >= 6 });
  return items;
}

export function SportHourlyTimeline({ scores, hours, sport, localHour, bestHour, windUnit = "kmh", selectedHour, onSelectHour }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const visRef = useRef<HTMLDivElement>(null);
  const [showDetails, setShowDetails] = useState(false);
  const isMountain = MOUNTAIN_SPORTS.has(sport);
  const hourMap = new Map(hours.map((h) => [h.hour, h]));

  useEffect(() => {
    if (!scrollRef.current) return;
    const now = selectedHour ?? localHour ?? new Date().getHours();
    const idx = scores.findIndex((s) => s.hour >= now);
    if (idx > 0) {
      const container = scrollRef.current;
      const child = container.children[idx] as HTMLElement | undefined;
      if (child) {
        container.scrollLeft = child.offsetLeft - container.offsetWidth / 2 + child.offsetWidth / 2;
      }
    }
  }, [scores, localHour, selectedHour]);

  useEffect(() => {
    if (!showDetails || !scrollRef.current) return;
    if (detailRef.current) detailRef.current.scrollLeft = scrollRef.current.scrollLeft;
    if (visRef.current) visRef.current.scrollLeft = scrollRef.current.scrollLeft;
  }, [showDetails]);

  const handleMainScroll = () => {
    if (!scrollRef.current) return;
    if (detailRef.current) detailRef.current.scrollLeft = scrollRef.current.scrollLeft;
    if (visRef.current) visRef.current.scrollLeft = scrollRef.current.scrollLeft;
  };

  return (
    <div className="w-full">
      <div
        ref={scrollRef}
        onScroll={handleMainScroll}
        className="flex gap-1.5 overflow-x-auto pb-3 snap-x snap-mandatory scrollbar-thin"
        role="list"
        aria-label={`Hourly ${sport} conditions`}
      >
        {scores.map((s) => {
          // A withheld v3 hour (Closed, or No lift access) shows the word, never a number.
          const withheld = skiHourWithheldLabel(sport, s.st);
          const hasScore = !withheld && typeof s.score === "number" && Number.isFinite(s.score);
          const rating = s.isDay && hasScore
            ? ratingFromScore(s.score as number, sport)
            : null;
          const hourData = hourMap.get(s.hour);
          const isBest = s.isDay && hasScore && bestHour != null && s.hour === bestHour && (localHour === undefined || s.hour >= localHour);
          const hasRange = hasScore && isSkiSport(sport) &&
            typeof s.lo === "number" && Number.isFinite(s.lo) &&
            typeof s.hi === "number" && Number.isFinite(s.hi);

          return (
            <div
              key={s.hour}
              role="listitem"
              className={`flex-shrink-0 w-[72px] snap-start rounded-[10px] border text-center ${
                onSelectHour && selectedHour === s.hour ? "border-teal ring-2 ring-teal/40 bg-white" : isBest
                  ? "border-gold ring-1 ring-gold/30 bg-white"
                  : !s.isDay ? "border-slate-200 bg-slate-50" : "border-border-light bg-white"
              }`}
            >
              {onSelectHour && <button type="button" className="min-h-11 w-full rounded-t-[9px] px-1 text-[10px] font-bold text-navy focus-visible:outline-2 focus-visible:outline-teal" aria-label={`Select ${String(s.hour).padStart(2, '0')}:00 forecast`} aria-pressed={selectedHour === s.hour} onClick={() => onSelectHour(s.hour)}>{formatHour(s.hour)}</button>}
              <div className={`px-1.5 pt-2 pb-0.5 ${onSelectHour ? 'hidden' : ''}`}>
                <div className="text-[10px] font-semibold text-text-muted uppercase tracking-[0.5px]">
                  {formatHour(s.hour)}
                </div>
              </div>

              {hourData && (
                <div
                  className={`text-[16px] leading-none ${!s.isDay ? "opacity-40" : ""}`}
                  aria-label={weatherCodeLabel(hourData.weather.weather_code)}
                >
                  {weatherCodeEmoji(hourData.weather.weather_code)}
                </div>
              )}

              {withheld ? (
                <div
                  className="mx-1 mt-1 rounded-[6px] border border-slate-200 bg-slate-100 px-1 py-1.5 text-[9px] font-semibold leading-tight text-slate-600"
                  data-ski-withheld={withheld}
                >
                  {withheld}
                </div>
              ) : s.isDay && rating ? (
                <>
                  <div
                    className={`mx-1 mt-1 rounded-[6px] py-0.5 ${rating.bg} ${rating.text}`}
                    title={hasRange ? skiLikelyRange10(s.lo, s.hi) ?? undefined : undefined}
                  >
                    <div className="text-[11px] font-bold tabular-nums">{scoreOutOf100(s.score as number)}</div>
                    <div className="text-[8px] uppercase tracking-[0.5px] opacity-90">{hourLabel(s.score as number, sport, rating)}</div>
                  </div>
                  {hasRange && <HourRange lo={s.lo as number} hi={s.hi as number} score={s.score as number} />}
                  {hourData && (
                    <div className="px-1 py-1 space-y-0">
                      {sportMetrics(hourData, sport, windUnit).map((m, i) => (
                        <div key={i} className="text-[9px] text-text-muted leading-[1.4]">{m}</div>
                      ))}
                    </div>
                  )}
                </>
              ) : s.isDay ? (
                <div className="mx-1 mt-1 rounded-[6px] bg-slate-100 px-1 py-2 text-[9px] font-semibold text-slate-600">
                  Unavailable
                </div>
              ) : (
                <div className="px-1.5 py-2 text-[9px] text-text-muted">Dark</div>
              )}

              {isBest && (
                <div className="text-[8px] font-bold text-gold uppercase tracking-[1px] pb-1.5">Best</div>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={() => setShowDetails(!showDetails)}
        className="flex items-center gap-1.5 text-[11px] font-medium text-text-muted hover:text-navy transition-colors mt-1"
        aria-expanded={showDetails}
      >
        <svg
          className={`w-3 h-3 transition-transform ${showDetails ? "rotate-90" : ""}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        Weather details
      </button>

      {showDetails && (
        <>
        <div
          ref={detailRef}
          className="flex gap-1.5 overflow-x-auto pb-2 mt-2 scrollbar-thin"
          role="list"
          aria-label="Hourly weather details"
        >
          {scores.map((s) => {
            const hourData = hourMap.get(s.hour);
            if (!hourData) return <div key={s.hour} className="flex-shrink-0 w-[72px]" />;
            const metrics = secondaryMetrics(hourData, windUnit);
            return (
              <div
                key={s.hour}
                className={`flex-shrink-0 w-[72px] rounded-[8px] border px-1 py-1.5 text-center ${
                  !s.isDay ? "border-slate-100 bg-slate-50/50 opacity-50" : "border-border-light bg-warm-bg"
                }`}
              >
                <div className="text-[9px] font-semibold text-text-muted mb-0.5">{formatHour(s.hour)}</div>
                {metrics.map((m, i) => (
                  <div key={i} className={`text-[9px] leading-[1.5] ${m.highlight ? "font-semibold text-blue-600" : "text-text-muted"}`}>
                    {m.value}
                    <span className="opacity-60 ml-0.5">{m.label.toLowerCase()}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {isMountain && (
          <>
            <div className="text-[10px] font-semibold text-text-muted uppercase tracking-[0.8px] mt-3 mb-1">
              Visibility
            </div>
            <div
              ref={visRef}
              className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-thin"
              role="img"
              aria-label="Hourly visibility quality"
            >
              {scores.map((s) => {
                const hourData = hourMap.get(s.hour);
                if (!s.isDay || !hourData) {
                  return <div key={s.hour} className="flex-shrink-0 w-[72px] h-5 rounded-[4px] bg-slate-100" />;
                }
                const f = _factors.visibilityFactor(hourData.weather);
                const cloud = hourData.weather.cloud_cover_pct;
                return (
                  <div
                    key={s.hour}
                    className="flex-shrink-0 w-[72px] h-5 rounded-[4px] flex items-center justify-center text-[8px] font-bold text-white"
                    style={{ backgroundColor: visColour(f, cloud) }}
                    title={`${formatHour(s.hour)}: ${visLabel(f, cloud)}`}
                  >
                    {visLabel(f, cloud)}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-3 mt-1.5 text-[10px] text-text-muted flex-wrap">
              {([
                ["#059669", "Bluebird"],
                ["#10b981", "Clear"],
                ["#0d9488", "Good"],
                ["#d97706", "Moderate"],
                ["#ea580c", "Flat light"],
                ["#dc2626", "Fog"],
              ] as const).map(([colour, label]) => (
                <span key={label} className="flex items-center gap-1">
                  <span className="inline-block w-2.5 h-2.5 rounded-[2px]" style={{ backgroundColor: colour }} />
                  {label}
                </span>
              ))}
            </div>
          </>
        )}
        </>
      )}
    </div>
  );
}
